import type { MaybeRefOrGetter } from 'vue'
import {
  leaderboardDocumentationPath,
  leaderboardHowItWorksPath,
  leaderboardPageHero,
} from '#leaderboards/composables/useLeaderboardPageHero'
import type { LeaderboardPageHeroKey } from '#leaderboards/composables/useLeaderboardPageHero'

export const LEADERBOARD_OG_IMAGE_PATH = '/images/public-research/market-maker-leaderboard.jpg'

export function getLeaderboardOgImageUrl(domain: string): string {
  return `${domain}${LEADERBOARD_OG_IMAGE_PATH}`
}

type LeaderboardSeoKey = LeaderboardPageHeroKey | 'documentation' | 'how-it-works'

export interface LeaderboardSeoContent {
  title: string
  description: string
}

const leaderboardStaticSeo: Record<'documentation' | 'how-it-works', LeaderboardSeoContent & { headTitle: string }> = {
  'documentation': {
    title: 'Leaderboard Documentation | Forgd',
    description: 'Full documentation for the Forgd Market Maker Leaderboard.',
    headTitle: 'Leaderboards Documentation',
  },
  'how-it-works': {
    title: 'How the Market Maker Leaderboard Works | Forgd',
    description: 'How the Forgd Market Maker Leaderboard works: where the data comes from and how rankings are calculated.',
    headTitle: 'How it Works',
  },
}

const leaderboardSeoKeyByPath: Record<string, LeaderboardSeoKey> = {
  '/market-maker-leaderboard': 'performance-index',
  '/market-maker-leaderboard/depth': 'depth',
  '/market-maker-leaderboard/volume': 'volume',
  '/market-maker-leaderboard/spread': 'spread',
  '/market-maker-leaderboard/kpi': 'kpi',
  [leaderboardDocumentationPath]: 'documentation',
  [leaderboardHowItWorksPath]: 'how-it-works',
}

function normalizeLeaderboardPath(path: string): string {
  const pathOnly = path.split('?')[0]?.split('#')[0] ?? path
  if (pathOnly.length > 1 && pathOnly.endsWith('/')) {
    return pathOnly.slice(0, -1)
  }
  return pathOnly
}

export function getLeaderboardSeoContent(path: string): LeaderboardSeoContent | null {
  const seoKey = leaderboardSeoKeyByPath[normalizeLeaderboardPath(path)]
  if (!seoKey) {
    return null
  }

  if (seoKey === 'documentation' || seoKey === 'how-it-works') {
    const { title, description } = leaderboardStaticSeo[seoKey]
    return { title, description }
  }

  const hero = leaderboardPageHero[seoKey]
  return {
    title: hero.seoTitle,
    description: hero.description,
  }
}

export function getLeaderboardHeadTitle(path: string): string | null {
  const seoKey = leaderboardSeoKeyByPath[normalizeLeaderboardPath(path)]
  if (!seoKey) {
    return null
  }

  if (seoKey === 'documentation' || seoKey === 'how-it-works') {
    return leaderboardStaticSeo[seoKey].headTitle
  }

  return leaderboardPageHero[seoKey].headTitle
}

export function useLeaderboardSeo(
  title: MaybeRefOrGetter<string | undefined>,
  description: MaybeRefOrGetter<string | undefined>,
) {
  const route = useRoute()
  const { appFrontEndDomain } = useRuntimeConfig().public
  const ogImage = getLeaderboardOgImageUrl(String(appFrontEndDomain))

  useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogImageType: 'image/jpeg',
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogType: 'website',
    ogSiteName: 'Forgd',
    ogUrl: () => `${appFrontEndDomain}${route.path}`,
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: ogImage,
  })

  return { ogImage }
}
